import type { GroupPrivate } from '@repo/types/groups';

import { cn } from '@repo/lib/cn';
import { BarChartBig, BookIcon, CalendarDaysIcon, CalendarPlus, ClipboardListIcon, Folder, LayoutListIcon } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import CreateMeeting from '@/components/features/calendar/form/CreateMeeting';
import CreatePost from '@/components/features/manage/manage-posts/CreatePost';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useQuizInitialization } from '@/hooks/quiz';
import { useCredentials } from '@/store/auth-store';

/**
 * Props for the GroupQuickMenu component.
 * @property {string} [className] - Optional CSS class for styling.
 * @property {GroupPrivate} [group] - The group data for quick actions.
 */
interface GroupQuickMenuProps {
  group?: GroupPrivate;
}

export default function GroupQuickMenu(groupQuickMenuProps: GroupQuickMenuProps) {
  const { group } = groupQuickMenuProps;
  const navigate = useNavigate();
  const quizInitialization = useQuizInitialization();

  const [openCreateEvent, setOpenCreateEvent] = useState<boolean>(false);
  const [openPostAdd, setOpenPostAdd] = useState<boolean>(false);

  const { lvl } = useCredentials();

  const handleOpenMeetingForm = () => {
    setOpenCreateEvent(true);
  };

  const handleOpenQuizForm = () => {
    quizInitialization.initializeQuiz(
      {
        name: `Quiz grupy ${group?.name}`,
      },
      {
        groupIds: group ? [group.id] : [],
      }
    );
  };

  const handleOpenPostForm = () => {
    setOpenPostAdd(true);
  };

  return (
    <>
      <CreatePost
        open={openPostAdd}
        onOpenChange={setOpenPostAdd}
        postOptions={{
          groupId: group?.id ?? null,
        }}
        defaultValues={{
          groups: group ? [group] : undefined,
          global: false,
        }}
      />
      <CreateMeeting
        open={openCreateEvent}
        onOpenChange={setOpenCreateEvent}
        defaultValues={{
          groups: group ? [group] : [],
          name: `Spotkanie grupy ${group?.name}`,
        }}
      />
      <div className={'border-border-primary bg-background flex w-full flex-col rounded-2xl border p-4'}>
        <div className={'space-y-4'}>
          {/* teacher menu */}
          {lvl > 0 && (
            <div className="space-y-2">
              <Label className={cn('text-text-tertiary flex font-medium')}>Szybkie akcje</Label>
              {/* teacher menu content */}
              <div className={'space-y-0.5'}>
                <Button variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} onClick={handleOpenMeetingForm}>
                  <CalendarPlus /> Dodaj spotkanie
                </Button>
                <Button variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} disabled={!group?.id} onClick={() => navigate(`/groups/${group?.id}/attendance`)}>
                  <CalendarDaysIcon /> Frekwencja
                </Button>
                <Button variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} onClick={handleOpenQuizForm}>
                  <LayoutListIcon /> Dodaj quiz
                </Button>
                <Button variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} onClick={handleOpenPostForm}>
                  <BookIcon /> Dodaj post
                </Button>
              </div>
            </div>
          )}
          {/* all users menu */}
          <div className="space-y-2">
            <Label className={cn('text-text-tertiary flex font-medium')}>Menu</Label>
            <div className={'flex w-full flex-col space-y-0.5'}>
              <Button
                disabled={!group?.directoryId}
                variant={'ghost'}
                size={'sm'}
                className={'w-full shrink-0 justify-start'}
                onClick={() => navigate(`/fs/group/${group?.id}/dir/${group?.directoryId}`)}
              >
                <Folder /> Pliki
              </Button>
              <Button variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} disabled={!group?.id} onClick={() => navigate(`/groups/${group?.id}/grades`)}>
                <BarChartBig /> Oceny
              </Button>
              <Button disabled={!group?.id} variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} onClick={() => navigate(`/quizzes?groupId=${group?.id}`)}>
                <LayoutListIcon /> Quizy
              </Button>
              <Button disabled={!group?.id} variant={'ghost'} size={'sm'} className={'w-full shrink-0 justify-start'} onClick={() => navigate(`/assignments?groupId=${group?.id}`)}>
                <ClipboardListIcon /> Zadania
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
